import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Consent, StatusBadge, Eyebrow } from "@/components/site/Bits";
import { ContactList } from "@/components/site/Footer";

const TicketLookup = () => {
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [t, setT] = useState<any>(null);
  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    try { setT((await api.post("/support/tickets/lookup", { reference: ref, email })).data); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="card-lux p-6" data-testid="ticket-lookup">
      <p className="label">Check a ticket</p>
      <form onSubmit={go} className="mt-2 space-y-3">
        <input required value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="TL-T-XXXXXXX" className="field font-mono" data-testid="ticket-lookup-ref" />
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="field" data-testid="ticket-lookup-email" />
        <button className="btn-ghost w-full" data-testid="ticket-lookup-submit">View ticket</button>
      </form>
      {t && (
        <div className="mt-5 space-y-3" data-testid="ticket-lookup-result">
          <div className="flex items-center justify-between"><p className="font-semibold">{t.subject}</p><StatusBadge status={t.status} /></div>
          {t.messages.map((m: any, i: number) => (
            <div key={i} className={`rounded-xl p-3 text-sm ${m.from === "team" ? "bg-navy text-white" : "bg-stone-canvas"}`}>
              <p className="text-[10px] uppercase tracking-wider opacity-60">{m.from === "team" ? "Trust Logistics" : "You"} · {fmtDate(m.at)}</p>
              <p className="mt-1 whitespace-pre-line">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Support() {
  const [f, setF] = useState({ name: "", email: "", phone: "", category: "general", subject: "", message: "", booking_reference: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState("");
  const set = (k: string) => (e: React.ChangeEvent<any>) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agree) return toast.error("Please accept the privacy policy");
    setBusy(true);
    try {
      const body: any = { ...f, accept_privacy: agree };
      if (!body.booking_reference) delete body.booking_reference;
      if (!body.phone) delete body.phone;
      setRef((await api.post("/support/tickets", body)).data.reference);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Customer support" title="How can we help?" intro="Open a support ticket and our team will reply by email. Live chat is not offered — every message goes to a real, monitored inbox." />
      <div className="container-x grid gap-10 py-14 lg:grid-cols-[1fr_380px]">
        {ref ? (
          <div className="card-lux space-y-3 p-8" data-testid="ticket-success">
            <CheckCircle2 className="text-emerald-600" size={30} />
            <h2 className="text-2xl">Ticket received</h2>
            <p className="text-sm text-navy/65">Your reference is <span className="font-mono font-bold" data-testid="ticket-reference">{ref}</span>. You will receive replies by email, and can check the ticket here anytime.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="card-lux space-y-4 p-6 sm:p-8" data-testid="ticket-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required placeholder="Full name" value={f.name} onChange={set("name")} className="field" data-testid="ticket-name" />
              <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className="field" data-testid="ticket-email" />
              <input placeholder="Phone / WhatsApp (optional)" value={f.phone} onChange={set("phone")} className="field" data-testid="ticket-phone" />
              <select value={f.category} onChange={set("category")} className="field" data-testid="ticket-category">
                {["general", "booking", "accommodation", "vehicle", "payment", "other"].map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
              </select>
              <input required placeholder="Subject" value={f.subject} onChange={set("subject")} className="field sm:col-span-2" data-testid="ticket-subject" />
              <input placeholder="Booking reference (optional)" value={f.booking_reference} onChange={set("booking_reference")} className="field font-mono sm:col-span-2" data-testid="ticket-booking-ref" />
            </div>
            <textarea required rows={6} placeholder="Your message (min 10 characters)" value={f.message} onChange={set("message")} className="field" data-testid="ticket-message" />
            <Consent testid="ticket-consent" checked={agree} onChange={setAgree} text={<>I agree to the <Link to="/policies/privacy" className="underline">Privacy Policy</Link>.</>} />
            <button disabled={busy} className="btn-navy" data-testid="ticket-submit">{busy && <Loader2 size={16} className="animate-spin" />} Submit ticket</button>
          </form>
        )}
        <div className="space-y-6">
          <div className="card-lux p-6"><Eyebrow>Direct contact</Eyebrow><div className="mt-4"><ContactList /></div></div>
          <TicketLookup />
        </div>
      </div>
    </div>
  );
}
